// @ts-nocheck
// Pure rules for the paid Rezepte hub. No DOM.
// Category tiles come only from real recipe data: meal_slot, total time, macros,
// product tags, and zutat names. Nothing is inferred from titles or descriptions.
// Cuisines are exact product tags; a tile appears once enough recipes carry it.
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.vhpPlannerHubRules = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // Copy from snippets/vhp-i18n.liquid in the browser; the German default in node (tests).
  const T = (key, de, vars) => {
    const all = globalThis.vhpI18n;
    if (globalThis.vhpT && all && key in all) return globalThis.vhpT(key, vars);
    return vars ? de.replace(/\{(\w+)\}/g, (m, name) => (vars[name] == null ? '' : String(vars[name]))) : de;
  };

  // A theme tile needs at least this many recipes. Mahlzeit tiles show from 1.
  const MIN_TILE = 4;
  const QUICK_MAX_MIN = 15;
  // Protein kcal share (protein g × 4 / kcal). ~30 of 120 recipes today.
  const PROTEIN_SHARE = 0.35;
  const SEEN_MAX = 1000;

  const MEAL = [
    { id: 'slot-breakfast', slot: 'breakfast', label: T('hub.breakfast', "Frühstück") },
    { id: 'slot-main', slot: 'main', label: T('hub.mains', "Hauptgerichte") },
    { id: 'slot-snack', slot: 'snack', label: T('hub.snacks', "Snacks") },
  ];

  // Main ingredients by zutat name. Rows that are only a by-product
  // (Pastawasser, Kartoffelstärke, Kichererbsenmehl, …) do not count.
  const INGREDIENT_SKIP = /(wasser|mehl|stärke|starke|pulver|brühe|bruhe|fond)/;
  const INGREDIENTS = [
    { id: 'ing-tofu', label: 'Tofu', re: /tofu/ },
    { id: 'ing-tempeh', label: 'Tempeh', re: /tempeh/ },
    { id: 'ing-hack', label: T('hub.hack', "Veganes Hack"), re: /\bhack(fleisch)?\b|sojagranulat|sojaschnetzel/ },
    { id: 'ing-legumes', label: T('hub.legumes', "Hülsenfrüchte"), re: /linse|kichererbse|bohne|edamame/ },
    { id: 'ing-pasta', label: T('hub.pasta', "Pasta & Nudeln"), re: /pasta|nudel|spaghetti|penne|fusilli|makkaroni|maccheroni|lasagne|tagliatelle|rigatoni/ },
    { id: 'ing-potato', label: T('hub.potato', "Kartoffel"), re: /kartoffel/ },
  ];

  // Exact product tags only (case/umlaut-insensitive). No aliases.
  const CUISINES = [
    'Asiatisch',
    'Italienisch',
    'Mexikanisch',
    'Indisch',
    'Türkisch',
    'Mediterran',
    'Griechisch',
    'Orientalisch',
    'Japanisch',
    'Koreanisch',
    'Thailändisch',
    'Deutsch',
  ];
  const MEAL_PREP_TAGS = ['meal prep', 'meal-prep', 'mealprep'];

  const fold = (value) =>
    String(value ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ß/g, 'ss')
      .trim()
      .toLowerCase();

  const productId = (id) => {
    const match = String(id ?? '').match(/(c?\d+)$/);
    return match ? match[1] : '';
  };

  const tagSet = (card) => new Set((Array.isArray(card?.tags) ? card.tags : []).map(fold).filter(Boolean));

  const ingredientNames = (card) =>
    (Array.isArray(card?.ingredients) ? card.ingredients : [])
      .map((row) => fold(typeof row === 'string' ? row : row?.n))
      .filter(Boolean);

  const isQuick = (card) => {
    const t = Number(card?.time);
    return Number.isFinite(t) && t > 0 && t <= QUICK_MAX_MIN;
  };

  const isProteinRich = (card) => {
    const kcal = Number(card?.kcal);
    const protein = Number(card?.protein);
    return kcal > 0 && Number.isFinite(protein) && (protein * 4) / kcal >= PROTEIN_SHARE;
  };

  const isMealPrep = (card) => {
    const tags = tagSet(card);
    return MEAL_PREP_TAGS.some((tag) => tags.has(tag));
  };

  const hasIngredient = (card, re) =>
    ingredientNames(card).some((name) => re.test(name) && !INGREDIENT_SKIP.test(name));

  // The planner's main-ingredient key (first matching rule), or null.
  const mainIngredient = (card) => INGREDIENTS.find((rule) => hasIngredient(card, rule.re))?.id || null;

  const hasCuisine = (card, cuisine) => tagSet(card).has(fold(cuisine));

  const cuisineId = (cuisine) => `cuisine-${fold(cuisine).replace(/[^a-z0-9]+/g, '-')}`;

  // Every theme rule, in display order before sorting by size.
  const themeRules = () => [
    { id: 'quick', label: T('hub.quick', "Schnell"), note: T('hub.quick_note', "bis {n} Min", { n: String(QUICK_MAX_MIN) }), test: isQuick },
    { id: 'protein', label: T('hub.protein', "Proteinbomben"), test: isProteinRich },
    { id: 'mealprep', label: T('hub.mealprep', "Meal-Prep"), test: isMealPrep },
    ...INGREDIENTS.map((rule) => ({
      id: rule.id,
      label: rule.label,
      kind: 'ingredient',
      test: (card) => hasIngredient(card, rule.re),
    })),
    ...CUISINES.map((cuisine) => ({
      id: cuisineId(cuisine),
      label: T(`hub.cuisine_${fold(cuisine).replace(/[^a-z0-9]+/g, '-')}`, cuisine),
      kind: 'cuisine',
      test: (card) => hasCuisine(card, cuisine),
    })),
  ];

  const idsWhere = (cards, test) => {
    const out = [];
    const seen = new Set();
    (cards || []).forEach((card) => {
      const id = productId(card?.id);
      if (!id || seen.has(id) || !test(card)) return;
      seen.add(id);
      out.push(id);
    });
    return out;
  };

  // { meal: [...], themes: [...] }; each tile is { id, label, ids }.
  // Theme order: Schnell, Proteinbomben, Meal-Prep, then ingredients and cuisines by size.
  const categories = (cards, opts) => {
    const min = Number(opts?.min) > 0 ? Number(opts.min) : MIN_TILE;
    const meal = MEAL.map((row) => ({
      id: row.id,
      label: row.label,
      group: 'meal',
      ids: idsWhere(cards, (card) => card?.slot === row.slot),
    })).filter((row) => row.ids.length > 0);

    const all = themeRules().map((rule) => ({
      id: rule.id,
      label: rule.label,
      note: rule.note || '',
      kind: rule.kind || 'theme',
      group: 'themes',
      ids: idsWhere(cards, rule.test),
    }));
    const fixed = all.filter((row) => row.kind === 'theme' && row.ids.length >= min);
    const rest = all
      .filter((row) => row.kind !== 'theme' && row.ids.length >= min)
      .sort((a, b) => b.ids.length - a.ids.length);
    return { meal, themes: fixed.concat(rest) };
  };

  // Cuisine tag counts, including the ones below the tile minimum (for reporting).
  const cuisineCounts = (cards) =>
    CUISINES.map((cuisine) => ({ tag: cuisine, count: idsWhere(cards, (card) => hasCuisine(card, cuisine)).length }));

  // Appends ids (deduped, order = first time seen) and keeps the newest SEEN_MAX.
  const addSeen = (list, ids, max) => {
    const cap = Number(max) > 0 ? Number(max) : SEEN_MAX;
    const out = Array.isArray(list) ? list.map(productId).filter(Boolean) : [];
    const have = new Set(out);
    (ids || []).forEach((raw) => {
      const id = productId(raw);
      if (!id || /^c/.test(id) || have.has(id)) return;
      have.add(id);
      out.push(id);
    });
    return out.length > cap ? out.slice(out.length - cap) : out;
  };

  // The reel opens at `id` and keeps the rest of the order after it (wrapping around).
  const rotateTo = (ids, id) => {
    const list = Array.isArray(ids) ? ids.slice() : [];
    const at = list.indexOf(id);
    return at > 0 ? list.slice(at).concat(list.slice(0, at)) : list;
  };

  return {
    MIN_TILE,
    QUICK_MAX_MIN,
    PROTEIN_SHARE,
    SEEN_MAX,
    MEAL,
    INGREDIENTS,
    CUISINES,
    fold,
    isQuick,
    isProteinRich,
    isMealPrep,
    hasIngredient,
    mainIngredient,
    hasCuisine,
    categories,
    cuisineCounts,
    addSeen,
    rotateTo,
  };
});
